import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { DeviceListBpComponent } from '@features/shopping/components/device-list-bp/device-list-bp.component';
import {
  activeFiltersCount,
  calculateItemsToDelete,
  loadDeviceImagePlaceholder
} from '@models/functions.model';
import { CategoryInfo } from '@models/session-details';
import { ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import {
  CART_ITEM_TYPE_DEVICE,
  CART_ITEM_TYPE_FEATURE,
  CART_ITEM_TYPE_PLAN,
  CATEGORY_FILTER_SORT_CLASSES
} from '@models/shopping.model';
import { DeviceItemDataBP, DeviceItemDeviceBP, ShoppingFilterItem } from '@models/shopping.model';
import {
  DeviceData,
  DeviceItemData,
  DeviceItemDevice,
  FilterToken,
  ShoppingCategoryData,
  ShoppingCategoryItem,
  ShoppingFilterData,
  ShoppingSortData
} from '@models/shopping.model';
import { NgbDropdownModule, NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { FilterStateService } from '@services/filter-state/filter-state.service';
import { ModalService } from '@services/modal/modal.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { AssetCategoryFilterComponent } from '@shared-components/asset-filters/asset-category-filter/asset-category-filter.component';
import { FilterTokensComponent } from '@shared-components/asset-filters/filter-tokens/filter-tokens.component';
import { FiltersIndexComponent } from '@shared-components/asset-filters/filters-index/filters-index.component';
import { Observer, Subscription, debounceTime, distinctUntilChanged, skip, switchMap } from 'rxjs';
import {
  DataLoaderCaptureOverrideDirective,
  DataLoaderDirective,
  DataLoaderService,
  ViewService
} from 'tangoe-design-system';

import { AdminOverrideBadgeComponent } from '../admin-override/admin-override-badge/admin-override-badge.component';
import { AdminOverrideBannerComponent } from '../admin-override/admin-override-banner/admin-override-banner.component';

@Component({
  selector: 'ts-device-list',
  templateUrl: './device-list.component.html',
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [
    CommonModule,
    NgbTooltipModule,
    TranslationPipe,
    AsButtonDirective,
    DataLoaderDirective,
    AdminOverrideBadgeComponent,
    AdminOverrideBannerComponent,
    AssetCategoryFilterComponent,
    FiltersIndexComponent,
    FilterTokensComponent,
    NgbDropdownModule,
    DeviceListBpComponent
  ]
})
export class DeviceListComponent implements OnInit, OnDestroy {
  @ViewChild('confirmClearCartModal') confirm_clear_cart_template: TemplateRef<any>;
  device_list: DeviceItemData[] | undefined = undefined;
  bp_device_list: DeviceItemDataBP[] | undefined;
  is_bp_response: boolean | undefined;
  device_list_count: number = 0;
  device_in_cart: DeviceItemData | DeviceItemDataBP;
  unique_color_list_count: number = 0;
  filters: ShoppingFilterData[];
  filters_count: number = 0;
  categories: ShoppingCategoryData[];
  filter_tokens: FilterToken[];
  sort_options: string[];
  selected_sort_dropdown_value: string;
  current_sort: ShoppingSortData;
  filter_config_id: number;
  category_filter_sort_classes: string = CATEGORY_FILTER_SORT_CLASSES;
  show_no_results: boolean = false;
  shopping_cart: ShoppingCartData;
  in_cart_device: DeviceItemDevice | DeviceItemDeviceBP;
  previous_admin_override: boolean | undefined = undefined;
  private debounced_time: number;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activated_route: ActivatedRoute,
    private activity_manager_svc: ActivityManagerService,
    private activity_state_svc: ActivityStateService,
    private data_loader_svc: DataLoaderService,
    private filter_state_svc: FilterStateService,
    private modal_svc: ModalService,
    private ngb_modal_svc: NgbModal,
    private router: Router,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService,
    private view_svc: ViewService
  ) {
    const debounced_time = parseInt(
      activated_route.snapshot.queryParamMap.get('__debounced') ?? ''
    );
    this.debounced_time = !isNaN(debounced_time) ? debounced_time : 750;
    this.sort_options = this.translation_svc.getTranslatedDeviceSortValues();
  }

  ngOnInit(): void {
    this.activated_route.queryParamMap.subscribe((paramMap) => {
      this.filter_state_svc.filtersParam.keep_plan = paramMap.get('keep_plan') === 'true';
    });

    this.setDefaultSort();

    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart = res;
          this.filter_state_svc.filtersParam.include_admin_options =
            res.device_include_admin_options;

          if (this.shopping_cart.shopping_cart_items.length > 0) {
            let device: ShoppingCartItem = res.shopping_cart_items.find(
              (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_DEVICE
            )!;
            if (device) {
              this.filter_state_svc.filtersParam.categories = [
                {
                  value: device?.additional_info?.category!,
                  id: device.additional_info?.category_id!
                }
              ];
            }
            this.getDevices();
          } else this.getDevices();
        },
        error: (err: HttpErrorResponse) => {}
      })
    );

    this.subscriptions.add(
      this.filter_state_svc.filter_changes
        .pipe(
          skip(1),
          debounceTime(this.debounced_time),
          distinctUntilChanged(),
          switchMap(() => this.shopping_svc.getDevices(this.filter_state_svc.filtersParam))
        )
        .subscribe(this.deviceRequestSubscribeArgument())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.filter_state_svc.filtersParam.categories = [];
    this.filter_state_svc.filtersParam.filters = [];
  }

  get category_name(): string {
    return (
      this.categories &&
      this.categories[0].items.filter((category: ShoppingCategoryItem) => category?.active)[0]
        .display_name
    );
  }

  get is_admin(): boolean {
    return this.session_svc.getUserProfile()?.admin ?? false;
  }

  loadInitialImage(devices: DeviceItemDevice[]): string {
    const devices_with_uniq_color: DeviceItemDevice[] = this.getSortedColorList(devices);
    return devices_with_uniq_color.length ? devices_with_uniq_color[0].image_url : '';
  }

  getPlaceholder(product_category: string, event: any) {
    return loadDeviceImagePlaceholder(product_category, event);
  }

  getSortedColorList(deviceList: DeviceItemDevice[]): DeviceItemDevice[] {
    const uniq_colors = this.shopping_svc.uniqColorByName(deviceList);
    this.unique_color_list_count = uniq_colors.length;
    return uniq_colors.length == 1 ? uniq_colors : this.shopping_svc.sortColorByName(uniq_colors);
  }

  getAdminOverride(admin_override: boolean): void {
    let previous_admin_override: boolean | undefined =
      this.filter_state_svc.filtersParam.include_admin_options;

    this.setDefaultSort();
    this.filter_state_svc.filtersParam = {
      ...this.filter_state_svc.filtersParam,
      filters: [],
      filter_tokens: [],
      include_admin_options: admin_override,
      categories: []
    };

    if (
      previous_admin_override &&
      !admin_override &&
      this.shopping_cart.shopping_cart_items.find(
        (cart_item: ShoppingCartItem) =>
          (cart_item.type === CART_ITEM_TYPE_DEVICE ||
            cart_item.type === CART_ITEM_TYPE_PLAN ||
            cart_item.type === CART_ITEM_TYPE_FEATURE) &&
          cart_item.additional_info?.admin
      )
    )
      this.previous_admin_override = previous_admin_override;
    else this.previous_admin_override = undefined;
  }

  selectAsset(device_details: DeviceItemData | DeviceItemDataBP): void {
    if (
      this.shopping_cart.shopping_cart_items.length > 0 &&
      this.shopping_cart.shopping_cart_items.find(
        (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_DEVICE
      )
    ) {
      this.device_in_cart = device_details;
      let show_confirm_modal: boolean = true;
      device_details.devices.forEach((device: DeviceItemDevice) => {
        if (
          this.shopping_cart.shopping_cart_items.filter(
            (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE
          )[0].id == device.id
        ) {
          show_confirm_modal = false;
        }
      });
      if (show_confirm_modal) {
        this.modal_svc.confirmationDialog(
          {
            title: this.translation_svc.getTranslatedModalLabels()[0],
            message: this.translation_svc.getTranslatedModalLabels()[1],
            confirm_button_text: this.translation_svc.translate('continue'),
            cancel_button_text: this.translation_svc.translate('cancel'),
            onConfirm: () => {
              let ids_to_delete: number[] = calculateItemsToDelete(
                this.shopping_cart.shopping_cart_items
              );
              this.confirmModalClearCart(ids_to_delete);
              this.modal_svc.dismissAll();
            }
          },
          {
            size: 'dialog-centered',
            centered: true,
            backdrop: 'static'
          }
        );
      } else this.navigateToDetails(device_details);
    } else this.navigateToDetails(device_details);
  }

  sortDeviceList(sort_label: string): void {
    if (!this.device_list || (this.is_bp_response && !this.bp_device_list)) return;

    switch (sort_label) {
      case this.sort_options[0]: {
        this.current_sort = {
          field: 'price',
          dir: 'asc'
        };
        this.selected_sort_dropdown_value = this.sort_options[0];
        if (!this.is_bp_response)
          this.device_list = this.sortDevices(this.device_list, 'price', 'asc');
        break;
      }
      case this.sort_options[1]: {
        this.current_sort = {
          field: 'price',
          dir: 'desc'
        };
        this.selected_sort_dropdown_value = this.sort_options[1];
        if (!this.is_bp_response)
          this.device_list = this.sortDevices(this.device_list, 'price', 'desc');
        break;
      }
      case this.sort_options[2]: {
        this.current_sort = {
          field: 'brand',
          dir: 'asc'
        };
        this.selected_sort_dropdown_value = this.sort_options[2];
        if (!this.is_bp_response)
          this.device_list = this.sortDevices(this.device_list, 'brand', 'asc');
        break;
      }
      case this.sort_options[3]: {
        this.current_sort = {
          field: 'brand',
          dir: 'desc'
        };
        this.selected_sort_dropdown_value = this.sort_options[3];
        if (!this.is_bp_response)
          this.device_list = this.sortDevices(this.device_list, 'brand', 'desc');
        break;
      }
      case this.sort_options[4]: {
        this.current_sort = {
          field: 'carrier',
          dir: 'asc'
        };
        this.selected_sort_dropdown_value = this.sort_options[4];
        if (!this.is_bp_response)
          this.device_list = this.sortDevices(this.device_list, 'carrier', 'asc');
        break;
      }
      case this.sort_options[5]: {
        this.current_sort = {
          field: 'carrier',
          dir: 'desc'
        };
        this.selected_sort_dropdown_value = this.sort_options[5];
        if (!this.is_bp_response)
          this.device_list = this.sortDevices(this.device_list, 'carrier', 'desc');
        break;
      }
      default: {
        this.current_sort = {
          field: 'price',
          dir: 'asc'
        };
        this.selected_sort_dropdown_value = this.sort_options[0];
        if (!this.is_bp_response)
          this.device_list = this.sortDevices(this.device_list, 'price', 'asc');
        break;
      }
    }

    this.pinCartDevice();
  }

  openMobileFilters(content: TemplateRef<any>): void {
    this.ngb_modal_svc.open(content, { fullscreen: true });
  }

  private getDevices(): void {
    this.subscriptions.add(
      this.shopping_svc
        .getDevices(this.filter_state_svc.filtersParam)
        .subscribe(this.deviceRequestSubscribeArgument())
    );
  }

  private deviceRequestSubscribeArgument(): Partial<Observer<DeviceData>> {
    return {
      next: (res: DeviceData) => {
        this.is_bp_response = res.is_device_list_business_purpose_response;

        if (this.is_bp_response)
          this.bp_device_list = (res.items as DeviceItemDataBP[]).filter(
            (device: DeviceItemDataBP) => device.devices.length
          );
        else
          this.device_list = this.sortDevices(
            res.items as DeviceItemData[],
            this.current_sort.field,
            this.current_sort.dir
          );

        this.in_cart_device = this.is_bp_response
          ? (res.in_cart_device as DeviceItemDeviceBP)
          : res.in_cart_device;
        this.pinCartDevice();

        this.device_list_count = this.is_bp_response
          ? this.bp_device_list!.length
          : this.device_list!.length;
        this.show_no_results = this.device_list_count > 0 ? false : true;

        this.categories = res.category;
        this.updateCategoryWithAll();

        this.filters = res.filters ? res.filters.filter((f) => f.items.length > 0) : [];
        this.filter_tokens = res.filter_tokens;
        this.filters_count = activeFiltersCount(this.filters);

        // set model config id
        const modal_filter: ShoppingFilterData = this.filters.filter((item) => {
          return item.config.filter_name === 'model';
        })[0];
        this.filter_config_id = modal_filter?.config.filter_config_id ?? 0;

        this.activity_state_svc.setSteps(res.progress_bar_config.steps);

        this.view_svc.resetScrollPosition();
      },
      error: (error) => {}
    };
  }

  // TODO : make this a shared function (for accessories)
  private sortDevices(devices: DeviceItemData[], field: string, dir: string): DeviceItemData[] {
    switch (field) {
      case 'price':
        return dir === 'asc'
          ? devices.sort((a, b) =>
              a.onetime_minimum_price_fractional < b.onetime_minimum_price_fractional ? -1 : 1
            )
          : devices.sort((a, b) =>
              a.onetime_minimum_price_fractional > b.onetime_minimum_price_fractional ? -1 : 1
            );
      case 'brand':
        return dir === 'asc'
          ? devices.sort((a, b) => (a.manufacturer < b.manufacturer ? -1 : 1))
          : devices.sort((a, b) => (a.manufacturer > b.manufacturer ? -1 : 1));
      case 'carrier':
        return dir === 'asc'
          ? devices.sort((a, b) => (a.carrier.name < b.carrier.name ? -1 : 1))
          : devices.sort((a, b) => (a.carrier.name > b.carrier.name ? -1 : 1));
      default:
        return dir === 'asc'
          ? devices.sort((a, b) =>
              a.onetime_minimum_price_fractional < b.onetime_minimum_price_fractional ? -1 : 1
            )
          : devices.sort((a, b) =>
              a.onetime_minimum_price_fractional > b.onetime_minimum_price_fractional ? -1 : 1
            );
    }
  }

  private confirmModalClearCart(ids_to_delete: number[]): void {
    this.shopping_svc.clearSpecifiedCartItems(ids_to_delete).subscribe({
      next: (res: ShoppingCartData) => {
        this.shopping_cart = res;
        this.navigateToDetails(this.device_in_cart);
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  private navigateToDetails(device_details: DeviceItemData | DeviceItemDataBP) {
    this.shopping_svc
      .sendShoppingItems({
        ...this.shopping_cart,
        device_include_admin_options: this.previous_admin_override
          ? this.previous_admin_override
          : this.filter_state_svc.filtersParam.include_admin_options,
        orderTypeId: this.activity_state_svc.orderTypeId(),
        country_id: this.activity_state_svc.parameter('country')?.id,
        progress_stepper: this.activity_manager_svc
          .state()
          .currentStep(this.activity_manager_svc.state().activeStepIndex())
      })
      .subscribe({
        next: (res: ShoppingCartData) => {
          this.activity_state_svc.setParameter(
            'device_include_admin_options',
            res.device_include_admin_options
          );
          this.data_loader_svc.toggle(true);
          this.router.navigate([`/shop/${this.activity_state_svc.orderTypeId()}/device-details`], {
            queryParams: {
              carrierId: this.is_bp_response
                ? (device_details as DeviceItemDataBP).devices[0].carrier.id
                : (device_details as DeviceItemData).carrier.id,
              name: this.is_bp_response
                ? (device_details as DeviceItemDataBP).devices[0].model
                : (device_details as DeviceItemData).model,
              filterConfigId: this.filter_config_id,
              categories: JSON.stringify(CategoryInfo.category_list),
              countryId: this.filter_state_svc.country?.id,
              keep_plan:
                this.activity_state_svc.orderTypeId() === 2
                  ? this.filter_state_svc.filtersParam.keep_plan
                  : null,
              deviceId: this.is_bp_response
                ? device_details.devices[0].id
                : this.in_cart_device?.id ?? undefined,
              isBpDevice: this.is_bp_response ?? undefined,
              filters: this.is_bp_response
                ? this.constructQueryParamFilters(this.filters, device_details)
                : undefined
            }
          });
        }
      });
  }

  private constructQueryParamFilters(
    filters: ShoppingFilterData[],
    device_details: DeviceItemData | DeviceItemDataBP
  ): string {
    let param_filters: ShoppingFilterItem[] = [];
    let asset: DeviceItemDevice | DeviceItemDeviceBP = device_details.devices[0];

    filters.forEach((filter: ShoppingFilterData) => {
      let shopping_filter_item: ShoppingFilterItem;
      switch (filter.config.filter_name) {
        case 'manufacturer':
          shopping_filter_item = {
            id: filter.config.filter_config_id,
            value: asset.manufacturer,
            filter_name: filter.config.filter_name
          };
          param_filters.push(shopping_filter_item);
          break;
        case 'model':
          shopping_filter_item = {
            id: filter.config.filter_config_id,
            value: asset.model,
            filter_name: filter.config.filter_name
          };
          param_filters.push(shopping_filter_item);
          break;
        case 'color':
          shopping_filter_item = {
            id: filter.config.filter_config_id,
            value: asset.color,
            filter_name: filter.config.filter_name
          };
          param_filters.push(shopping_filter_item);
          break;
        case 'size':
          if (asset.size) {
            shopping_filter_item = {
              id: filter.config.filter_config_id,
              value: asset.size,
              filter_name: filter.config.filter_name
            };
            param_filters.push(shopping_filter_item);
          }
          break;
        default:
          break;
      }
    });

    return JSON.stringify(param_filters);
  }

  private updateCategoryWithAll(): void {
    if (this.categories) {
      this.categories.forEach((category_data: ShoppingCategoryData) =>
        category_data.items.unshift({
          item: 'All',
          material_icon_class: 'all_inclusive',
          display_name: this.translation_svc.translate('all'),
          active: !!!category_data.items.find((category: ShoppingCategoryItem) => category?.active)
            ? true
            : false,
          other_group_items: []
        })
      );
    }
  }

  private pinCartDevice(): void {
    if (this.in_cart_device && this.device_list)
      this.device_list.forEach((device_data: DeviceItemData, index: number = 0) => {
        let in_cart: boolean = false;

        device_data.devices.forEach((device: DeviceItemDevice) => {
          if (device.id === this.in_cart_device.id) {
            in_cart = true;
            return;
          }
        });

        if (in_cart && this.device_list) {
          this.device_list[index].in_cart = true;
          this.device_list?.unshift(this.device_list.splice(index, 1)[0]);
          return;
        }

        index++;
      });
  }

  private setDefaultSort(): void {
    this.current_sort = { field: 'price', dir: 'asc' };
    this.selected_sort_dropdown_value = this.sort_options[0];
  }
}
